/**
 * HTTP client for the PHP API.
 *
 * One place that knows the API's location, envelope shape and error handling,
 * so a misconfigured path fails the same recognisable way everywhere.
 */

/**
 * Where the API lives, relative to the built client.
 *
 * The deployed layout is `<root>/app/` for the client and `<root>/server/` for
 * the PHP, so from a page at `…/app/` the API is one level up and then into
 * `server/`. Override with `<meta name="sircad-api" content="…">` in
 * index.html when they are not siblings.
 */
export function apiBase(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="sircad-api"]');
  const override = meta?.content?.trim();
  return override && override !== '' ? override : '../server/api/index.php';
}

export class ApiError extends Error {
  // Declared and assigned rather than written as a constructor parameter
  // property: node's type stripping cannot erase one of those, and the headless
  // checks import this module through node.
  readonly status: number;

  constructor(message: string, status = 0) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface Envelope<T> {
  ok?: boolean;
  data?: T;
  error?: string;
}

async function send<T>(
  action: string,
  init: RequestInit,
  params: Record<string, string | number> = {},
): Promise<T> {
  const q = new URLSearchParams({
    action,
    ...Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)])),
  });
  const url = `${apiBase()}?${q}`;

  let res: Response;
  try {
    res = await fetch(url, { credentials: 'same-origin', ...init });
  } catch (err) {
    throw new ApiError(`cannot reach ${url} (${(err as Error).message})`);
  }

  // A wrong path returns the webserver's 404 page. Parsing that as JSON gives
  // a syntax error naming neither the URL nor the status, so handle it here.
  const text = await res.text();
  let body: Envelope<T>;
  try {
    body = JSON.parse(text) as Envelope<T>;
  } catch {
    const snippet = text.trim().slice(0, 120).replace(/\s+/g, ' ');
    throw new ApiError(
      `${url} returned ${res.status} and was not JSON — check the API path. ${snippet}`,
      res.status,
    );
  }

  if (!body.ok) {
    throw new ApiError(body.error ?? `request failed (${res.status})`, res.status);
  }
  return body.data as T;
}

export function apiGet<T>(
  action: string,
  params: Record<string, string | number> = {},
): Promise<T> {
  return send<T>(action, { method: 'GET' }, params);
}

export function apiPost<T>(action: string, payload: unknown = {}): Promise<T> {
  return send<T>(action, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
